import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../api';

const GOLD = '#D4AF37';
const GOLD_LIGHT = '#E8C84A';
const GOLD_DIM = 'rgba(212,175,55,0.12)';
const GOLD_BORDER = 'rgba(212,175,55,0.35)';
const BG = '#050810';
const SURFACE = '#0B1120';
const CARD_BG = '#0F1929';
const BORDER = 'rgba(255,255,255,0.07)';

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
);

const PLANS = [
  {
    key: 'starter',
    name: 'Starter',
    price: '$49',
    period: '/mo',
    features: ['Online booking page', 'Appointment management', 'Client list', 'SMS & email reminders', 'Basic analytics'],
    badge: null,
  },
  {
    key: 'pro',
    name: 'Pro',
    price: '$99',
    period: '/mo',
    features: ['Everything in Starter', 'CRM & follow-ups', 'AI content generation', 'Social media posting', 'Advanced analytics', 'Priority support'],
    badge: 'MOST POPULAR',
  },
  {
    key: 'agency',
    name: 'Agency',
    price: '$299',
    period: '/mo',
    features: ['Everything in Pro', 'Multi-location support', 'Team management', 'White-label branding', 'API access', 'Dedicated account manager'],
    badge: null,
  },
];

const FOUNDING = {
  key: 'founding',
  name: 'Founding Member',
  price: '$199',
  period: ' one-time',
  features: ['Lifetime Pro rate ($29/mo)', 'Personal onboarding call', 'Founding member badge', 'First 5 bookings guaranteed', 'Early access to new features'],
  badge: 'LIMITED',
};

const PROFESSIONS = [
  'Barber', 'Stylist', 'Nail Tech', 'Esthetician', 'Lash Tech',
  'Makeup Artist', 'Massage Therapist', 'Personal Trainer', 'Coach', 'Other',
];

const STEP_LABELS = ['Account', 'Profile', 'Plan', 'Payment', 'Welcome'];

// ── Styles ──────────────────────────────────────────────────────────────
const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em',
  textTransform: 'uppercase', color: '#8892A4', marginBottom: 6,
};

const inputStyle = {
  width: '100%', background: '#111', border: '1px solid #252525',
  borderRadius: 4, padding: '13px 14px', color: '#fff', fontSize: 14,
  outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s',
};

const selectStyle = {
  ...inputStyle, appearance: 'none', cursor: 'pointer',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23666' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center',
  paddingRight: 36,
};

const cardStyle = {
  style: {
    base: {
      color: '#fff', fontSize: '16px', fontFamily: 'Inter, system-ui, sans-serif',
      '::placeholder': { color: '#555' },
    },
    invalid: { color: '#ef4444' },
  },
};

// ── Progress Bar ────────────────────────────────────────────────────────
function ProgressBar({ step }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, maxWidth: 480, margin: '0 auto 40px' }}>
      {STEP_LABELS.map((label, i) => {
        const active = i <= step;
        const current = i === step;
        return (
          <React.Fragment key={label}>
            {i > 0 && (
              <div style={{
                flex: 1, height: 2,
                background: i <= step ? GOLD : '#252525',
                transition: 'background 0.3s',
              }} />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 48 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: active ? GOLD : 'transparent',
                border: `2px solid ${active ? GOLD : '#333'}`,
                color: active ? '#000' : '#555',
                fontSize: 12, fontWeight: 800,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.3s',
                boxShadow: current ? `0 0 12px ${GOLD_DIM}` : 'none',
              }}>
                {i < step ? '✓' : i + 1}
              </div>
              <span style={{
                fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
                textTransform: 'uppercase', marginTop: 6,
                color: active ? GOLD : '#555',
              }}>
                {label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Step 1: Account ─────────────────────────────────────────────────────
function StepAccount({ form, onChange, onNext, error }) {
  const handleSubmit = e => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) return;
    if (!form.email.trim()) return;
    if (!form.password || form.password.length < 8) return;
    if (!form.businessName.trim()) return;
    onNext();
  };

  return (
    <form onSubmit={handleSubmit}>
      <h2 style={{ fontWeight: 900, fontSize: 22, letterSpacing: '-0.01em', marginBottom: 8, textAlign: 'center' }}>
        Create Your Account
      </h2>
      <p style={{ color: '#8892A4', fontSize: 14, textAlign: 'center', marginBottom: 32 }}>
        Get started with Mane in minutes.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={labelStyle}>First Name *</label>
          <input style={inputStyle} name="firstName" value={form.firstName} onChange={onChange} placeholder="First name" autoComplete="given-name" required />
        </div>
        <div>
          <label style={labelStyle}>Last Name *</label>
          <input style={inputStyle} name="lastName" value={form.lastName} onChange={onChange} placeholder="Last name" autoComplete="family-name" required />
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Email *</label>
        <input style={inputStyle} name="email" type="email" value={form.email} onChange={onChange} placeholder="you@business.com" autoComplete="email" required />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Phone</label>
        <input style={inputStyle} name="phone" type="tel" value={form.phone} onChange={onChange} placeholder="(555) 000-0000" autoComplete="tel" />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Password *</label>
        <input style={inputStyle} name="password" type="password" value={form.password} onChange={onChange} placeholder="At least 8 characters" autoComplete="new-password" required minLength={8} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
        <div>
          <label style={labelStyle}>Business Name *</label>
          <input style={inputStyle} name="businessName" value={form.businessName} onChange={onChange} placeholder="Your business" autoComplete="organization" required />
        </div>
        <div>
          <label style={labelStyle}>City</label>
          <input style={inputStyle} name="city" value={form.city} onChange={onChange} placeholder="City" autoComplete="address-level2" />
        </div>
      </div>

      {error && <ErrorBox message={error} />}

      <button type="submit" style={btnPrimary}>Continue</button>
    </form>
  );
}

// ── Step 2: Profile ─────────────────────────────────────────────────────
function StepProfile({ form, onChange, onNext, onBack }) {
  return (
    <div>
      <h2 style={{ fontWeight: 900, fontSize: 22, letterSpacing: '-0.01em', marginBottom: 8, textAlign: 'center' }}>
        Your Professional Profile
      </h2>
      <p style={{ color: '#8892A4', fontSize: 14, textAlign: 'center', marginBottom: 32 }}>
        Tell us about your business.
      </p>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Business Type *</label>
        <select style={selectStyle} name="profession" value={form.profession} onChange={onChange}>
          <option value="">Select your profession</option>
          {PROFESSIONS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Years of Experience</label>
        <input style={inputStyle} name="yearsExp" type="number" min="0" max="50" value={form.yearsExp} onChange={onChange} placeholder="e.g., 5" />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Bio (optional)</label>
        <textarea
          style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
          name="bio" value={form.bio} onChange={onChange}
          placeholder="A short description of you and your work..."
        />
      </div>

      <div style={{ marginBottom: 24 }}>
        <label style={labelStyle}>Instagram Handle (optional)</label>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#555', fontSize: 14 }}>@</span>
          <input style={{ ...inputStyle, paddingLeft: 30 }} name="instagram" value={form.instagram} onChange={onChange} placeholder="yourhandle" />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        <button type="button" onClick={onBack} style={btnSecondary}>Back</button>
        <button type="button" onClick={onNext} style={{ ...btnPrimary, flex: 1 }}>Continue</button>
      </div>
    </div>
  );
}

// ── Step 3: Plan Selection ──────────────────────────────────────────────
function StepPlan({ form, onChange, onNext, onBack, foundingSpotsLeft }) {
  const selectedPlan = form.plan;

  const PlanCard = ({ plan, selected, onClick }) => (
    <div
      onClick={onClick}
      style={{
        background: selected ? GOLD_DIM : CARD_BG,
        border: `2px solid ${selected ? GOLD : BORDER}`,
        borderRadius: 12, padding: '24px 20px', cursor: 'pointer',
        transition: 'all 0.2s',
        position: 'relative',
        transform: selected ? 'scale(1.02)' : 'scale(1)',
      }}
    >
      {plan.badge && (
        <div style={{
          position: 'absolute', top: -10, right: 16,
          background: plan.badge === 'LIMITED' ? '#ef4444' : GOLD,
          color: plan.badge === 'LIMITED' ? '#fff' : '#000',
          fontSize: 9, fontWeight: 800, letterSpacing: '0.12em',
          padding: '4px 12px', borderRadius: 100, textTransform: 'uppercase',
        }}>
          {plan.badge}
        </div>
      )}
      <h3 style={{ fontWeight: 800, fontSize: 18, marginBottom: 4 }}>{plan.name}</h3>
      <div style={{ marginBottom: 16 }}>
        <span style={{ fontSize: 32, fontWeight: 900, color: GOLD }}>{plan.price}</span>
        <span style={{ color: '#8892A4', fontSize: 14 }}>{plan.period}</span>
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {plan.features.map(f => (
          <li key={f} style={{ color: '#ccc', fontSize: 13, padding: '4px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: GOLD, fontSize: 12 }}>✓</span> {f}
          </li>
        ))}
      </ul>
      {selected && (
        <div style={{
          marginTop: 16, textAlign: 'center', color: GOLD,
          fontWeight: 800, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase',
        }}>
          Selected
        </div>
      )}
    </div>
  );

  return (
    <div>
      <h2 style={{ fontWeight: 900, fontSize: 22, letterSpacing: '-0.01em', marginBottom: 8, textAlign: 'center' }}>
        Choose Your Plan
      </h2>
      <p style={{ color: '#8892A4', fontSize: 14, textAlign: 'center', marginBottom: 32 }}>
        All plans include zero commission on bookings. Cancel anytime.
      </p>

      <div style={{ display: 'grid', gap: 16, marginBottom: 20 }}>
        {PLANS.map(plan => (
          <PlanCard
            key={plan.key}
            plan={plan}
            selected={selectedPlan === plan.key}
            onClick={() => onChange({ target: { name: 'plan', value: plan.key } })}
          />
        ))}
      </div>

      {/* Founding offer */}
      {foundingSpotsLeft > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(212,175,55,0.08) 0%, rgba(212,175,55,0.02) 100%)',
            border: `1px solid ${GOLD_BORDER}`,
            borderRadius: 12, padding: '16px 20px', textAlign: 'center', marginBottom: 12,
          }}>
            <span style={{ color: GOLD, fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
              Founding Offer — {foundingSpotsLeft} spots left
            </span>
          </div>
          <PlanCard
            plan={FOUNDING}
            selected={selectedPlan === 'founding'}
            onClick={() => onChange({ target: { name: 'plan', value: 'founding' } })}
          />
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
        <button type="button" onClick={onBack} style={btnSecondary}>Back</button>
        <button
          type="button"
          onClick={onNext}
          disabled={!selectedPlan}
          style={{ ...btnPrimary, flex: 1, opacity: selectedPlan ? 1 : 0.4, cursor: selectedPlan ? 'pointer' : 'not-allowed' }}
        >
          Continue to Payment
        </button>
      </div>
    </div>
  );
}

// ── Step 4: Payment (Stripe Elements) ───────────────────────────────────
function PaymentForm({ form, onSuccess, onBack, setError, error }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const selectedPlan = [...PLANS, FOUNDING].find(p => p.key === form.plan);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setError('');

    try {
      // Create payment method from card element
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
        billing_details: {
          name: `${form.firstName} ${form.lastName}`,
          email: form.email,
        },
      });

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      // Call signup endpoint with all form data + payment method
      const res = await api.post('/api/professionals/signup', {
        ...form,
        paymentMethodId: paymentMethod.id,
      });

      if (res.data.success) {
        // Store auth token
        localStorage.setItem('fu_token', res.data.token);
        onSuccess(res.data.slug);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <h2 style={{ fontWeight: 900, fontSize: 22, letterSpacing: '-0.01em', marginBottom: 8, textAlign: 'center' }}>
        Payment
      </h2>
      <p style={{ color: '#8892A4', fontSize: 14, textAlign: 'center', marginBottom: 32 }}>
        {selectedPlan ? `${selectedPlan.name} — ${selectedPlan.price}${selectedPlan.period}` : 'Complete your signup'}
      </p>

      <div style={{
        background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 8,
        padding: '20px 16px', marginBottom: 24,
      }}>
        <label style={{ ...labelStyle, marginBottom: 12 }}>Card Details</label>
        <div style={{
          background: '#111', border: '1px solid #252525', borderRadius: 4,
          padding: '14px', transition: 'border-color 0.15s',
        }}>
          <CardElement options={cardStyle} />
        </div>
      </div>

      {/* Order summary */}
      <div style={{
        background: GOLD_DIM, border: `1px solid ${GOLD_BORDER}`, borderRadius: 8,
        padding: '16px 20px', marginBottom: 24,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#ccc', fontSize: 14 }}>
            {selectedPlan?.name} Plan
          </span>
          <span style={{ color: GOLD, fontSize: 20, fontWeight: 900 }}>
            {selectedPlan?.price}{selectedPlan?.period}
          </span>
        </div>
      </div>

      {error && <ErrorBox message={error} />}

      <div style={{ display: 'flex', gap: 12 }}>
        <button type="button" onClick={onBack} style={btnSecondary} disabled={loading}>Back</button>
        <button type="submit" disabled={loading || !stripe} style={{ ...btnPrimary, flex: 1, opacity: loading ? 0.6 : 1 }}>
          {loading ? 'Processing...' : `Pay ${selectedPlan?.price || ''}`}
        </button>
      </div>

      <p style={{ color: '#555', fontSize: 11, textAlign: 'center', marginTop: 16 }}>
        Secured by Stripe. Your card is encrypted end-to-end.
      </p>
    </form>
  );
}

// ── Step 5: Success ─────────────────────────────────────────────────────
function StepSuccess({ slug, name }) {
  const profileUrl = `joinmane.com/${slug}`;
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(`https://${profileUrl}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{
        width: 80, height: 80, borderRadius: '50%',
        background: GOLD_DIM, border: `3px solid ${GOLD}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 24px', fontSize: 36,
      }}>
        🎉
      </div>

      <h1 style={{ fontWeight: 900, fontSize: 28, letterSpacing: '-0.02em', marginBottom: 8 }}>
        Welcome to Mane!
      </h1>
      <p style={{ color: '#8892A4', fontSize: 15, marginBottom: 32 }}>
        Your profile is live, {name}. Share it with your clients.
      </p>

      {/* Profile URL card */}
      <div style={{
        background: CARD_BG, border: `1px solid ${GOLD_BORDER}`, borderRadius: 12,
        padding: '24px 20px', marginBottom: 24, textAlign: 'left',
      }}>
        <label style={{ ...labelStyle, color: GOLD, marginBottom: 12 }}>Your Profile URL</label>
        <div style={{
          background: '#111', borderRadius: 6, padding: '14px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <span style={{ color: '#fff', fontSize: 15, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {profileUrl}
          </span>
          <button
            onClick={handleCopy}
            style={{
              background: copied ? 'rgba(16,185,129,0.15)' : GOLD_DIM,
              border: `1px solid ${copied ? 'rgba(16,185,129,0.4)' : GOLD_BORDER}`,
              color: copied ? '#10b981' : GOLD,
              padding: '8px 16px', borderRadius: 4, fontSize: 12,
              fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      {/* Next steps */}
      <div style={{
        background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 12,
        padding: '24px 20px', marginBottom: 32, textAlign: 'left',
      }}>
        <h3 style={{ fontWeight: 800, fontSize: 15, marginBottom: 16, color: GOLD }}>Next Steps</h3>
        {[
          { icon: '📋', text: 'Add your services & pricing' },
          { icon: '📅', text: 'Set your availability schedule' },
          { icon: '📱', text: 'Share your link on Instagram' },
          { icon: '👥', text: 'Import your existing clients' },
        ].map((item, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 0', borderBottom: i < 3 ? `1px solid ${BORDER}` : 'none',
          }}>
            <span style={{ fontSize: 18 }}>{item.icon}</span>
            <span style={{ color: '#ccc', fontSize: 14 }}>{item.text}</span>
          </div>
        ))}
      </div>

      <a
        href="/dash"
        style={{
          display: 'inline-block', width: '100%', textAlign: 'center',
          background: GOLD, color: '#000', fontWeight: 900, fontSize: 14,
          letterSpacing: '0.1em', textTransform: 'uppercase',
          padding: '18px', borderRadius: 4, textDecoration: 'none',
          transition: 'background 0.15s',
        }}
      >
        Go to Dashboard
      </a>
    </div>
  );
}

// ── Error Box ───────────────────────────────────────────────────────────
function ErrorBox({ message }) {
  return (
    <div style={{
      background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
      color: '#ef4444', fontSize: 13, padding: '12px 16px',
      borderRadius: 4, marginBottom: 20,
    }}>
      {message}
    </div>
  );
}

// ── Shared button styles ────────────────────────────────────────────────
const btnPrimary = {
  width: '100%', background: GOLD, color: '#000',
  fontWeight: 900, fontSize: 14, letterSpacing: '0.1em',
  textTransform: 'uppercase', padding: '18px', border: 'none',
  borderRadius: 4, cursor: 'pointer', transition: 'background 0.15s',
};

const btnSecondary = {
  padding: '18px 24px', background: 'transparent',
  border: `1px solid #333`, color: '#8892A4', fontWeight: 700,
  fontSize: 13, borderRadius: 4, cursor: 'pointer',
  letterSpacing: '0.06em', textTransform: 'uppercase',
};

// ── Main Component ──────────────────────────────────────────────────────
export default function ProSignup() {
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [slug, setSlug] = useState('');
  const [foundingSpots, setFoundingSpots] = useState(0);
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', password: '',
    businessName: '', city: '',
    profession: '', yearsExp: '', bio: '', instagram: '',
    plan: 'pro',
  });

  useEffect(() => {
    api.get('/api/founding/spots')
      .then(r => setFoundingSpots(r.data.spots_remaining || 0))
      .catch(() => {});
  }, []);

  // Check URL params for success redirect from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('step') === 'success') {
      setStep(4);
    }
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    setError('');
  };

  const goNext = () => {
    setError('');
    setStep(s => Math.min(s + 1, 4));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBack = () => {
    setError('');
    setStep(s => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSuccess = (newSlug) => {
    setSlug(newSlug);
    setStep(4);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div style={{
      background: 'transparent', minHeight: '100vh', color: '#fff',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      <Helmet>
        <title>Sign Up — Mane</title>
        <meta name="description" content="Join Mane — the premium booking platform for barbers, stylists, nail techs, and beauty professionals. Zero commission. Start in minutes." />
        <meta property="og:title" content="Join Mane — Start Your Professional Profile" />
        <meta property="og:description" content="Zero commission booking platform for beauty & grooming professionals." />
      </Helmet>

      {/* Header */}
      <div style={{
        padding: '24px', textAlign: 'center',
        borderBottom: `1px solid ${BORDER}`,
      }}>
        <a href="/" style={{
          color: GOLD, fontWeight: 900, fontSize: 16,
          letterSpacing: '0.2em', textTransform: 'uppercase',
          textDecoration: 'none',
        }}>
          MANE
        </a>
      </div>

      {/* Hero */}
      {step < 4 && (
        <section style={{
          padding: '48px 24px 24px', textAlign: 'center',
          position: 'relative',
        }}>
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            background: `radial-gradient(ellipse 80% 60% at 50% 0%, rgba(212,175,55,0.06) 0%, transparent 70%)`,
            pointerEvents: 'none',
          }} />
          <div style={{ position: 'relative' }}>
            <h1 style={{
              fontSize: 'clamp(28px, 6vw, 44px)', fontWeight: 900,
              lineHeight: 1.1, letterSpacing: '-0.02em',
              marginBottom: 12,
            }}>
              Start Growing with <span style={{ color: GOLD }}>Mane</span>
            </h1>
            <p style={{ color: '#8892A4', fontSize: 15, maxWidth: 420, margin: '0 auto' }}>
              The zero-commission platform for grooming & beauty professionals.
            </p>
          </div>
        </section>
      )}

      {/* Content */}
      <section style={{ maxWidth: 520, margin: '0 auto', padding: '32px 24px 80px' }}>
        {step < 4 && <ProgressBar step={step} />}

        <div style={{
          transition: 'opacity 0.2s ease',
          animation: 'fadeIn 0.3s ease',
        }}>
          {step === 0 && (
            <StepAccount form={form} onChange={handleChange} onNext={goNext} error={error} />
          )}

          {step === 1 && (
            <StepProfile form={form} onChange={handleChange} onNext={goNext} onBack={goBack} />
          )}

          {step === 2 && (
            <StepPlan
              form={form} onChange={handleChange}
              onNext={goNext} onBack={goBack}
              foundingSpotsLeft={foundingSpots}
            />
          )}

          {step === 3 && (
            <Elements stripe={stripePromise}>
              <PaymentForm
                form={form}
                onSuccess={handleSuccess}
                onBack={goBack}
                setError={setError}
                error={error}
              />
            </Elements>
          )}

          {step === 4 && (
            <StepSuccess slug={slug} name={form.firstName || 'Pro'} />
          )}
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        borderTop: `1px solid ${BORDER}`, padding: '24px',
        textAlign: 'center',
      }}>
        <p style={{ color: '#555', fontSize: 11 }}>
          © {new Date().getFullYear()} MANE · <a href="/privacy" style={{ color: '#555' }}>Privacy</a> · <a href="/terms" style={{ color: '#555' }}>Terms</a>
        </p>
      </footer>
    </div>
  );
}
